import { useEffect, useRef, useState } from "react"
import "./Calculator.css"
import { Accordion, Alert, Button, Form, Stack } from "react-bootstrap"
import { ICalculatorState, SectionWithFields } from "../Types/calculator"
import { DEFAULT_STATE, sections, allInputsValid, isFormComplete, VERSION_NUMBER } from "../Utilities/constants"
import { Results } from "./Results"
import { Errors } from "./Errors"
import _ from "lodash"

export default function Calculator() {
  const [state, setState] = useState<ICalculatorState>(() => {
    const savedState = localStorage.getItem("ROICalculatorState")
    return savedState ? JSON.parse(savedState) : _.cloneDeep(DEFAULT_STATE)
  })
  const [touched, setTouched] = useState<boolean>(false)
  const previousState = useRef<ICalculatorState>(state)

  console.log("STATE", state)

  const setSettings = (settings: Partial<ICalculatorState>) => {
    if (!touched) setTouched(true)
    const newState = { ...state, ...settings }
    localStorage.setItem("ROICalculatorState", JSON.stringify(newState))
    setState(newState)
  }

  const onChangeFormType = () => {
    const switchToSimple = !state.useSimpleForm

    let updates: Partial<ICalculatorState> = {}

    // preserves values for shared sections
    updates = _.cloneDeep(previousState.current)
    updates.useSimpleForm = switchToSimple
    updates.technologies = [...state.technologies]
    updates.fleetMiles = state.fleetMiles
    updates.fleetSize = state.fleetSize
    updates.calculatedCostPerUnit = state.calculatedCostPerUnit
    updates.useMilesPerVehicle = state.useMilesPerVehicle
    updates.crashMitigationEffectiveness = state.crashMitigationEffectiveness

    previousState.current = state

    setTouched(false)
    setSettings(updates)
  }

  const onCategorySelected = (category?: string) => {
    if (state.activeCategory === category) category = undefined
    const expandedSections = _.cloneDeep(state.expandedSections)
    if (category && !expandedSections.includes(category)) expandedSections.push(category)
    setSettings({ expandedSections, activeCategory: category })
  }

  const resetFormValues = () => {
    setState({ ...DEFAULT_STATE, useSimpleForm: state.useSimpleForm })
    setTouched(false)
    localStorage.removeItem("ROICalculatorState")
  }

  // changed schema from strings for most types to numbers, causes crash on load trying to do .toFixed()
  // so we need to just reset the state if they had a saved state
  useEffect(() => {
    const savedStateString = localStorage.getItem("ROICalculatorState")
    if (savedStateString) {
      const savedState: ICalculatorState = JSON.parse(savedStateString)
      if (savedState.versionNumber !== VERSION_NUMBER) {
        setState(DEFAULT_STATE)
        setTouched(false)
        localStorage.removeItem("ROICalculatorState")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Form>
      <div className="IncludeInExport PrintableHeader">Tech-Celerate Now ROI Calculator</div>
      <Accordion className="Categories" activeKey={state.activeCategory}>
        {sections.map((section) => {
          const { SectionComponent, showOnSimpleForm, name } = section
          const sectionConst = (<>
            <SectionComponent
              key={name}
              onCategorySelected={onCategorySelected}
              state={state}
              setSettings={setSettings}
              section={section as SectionWithFields<keyof ICalculatorState>}
            />
            <div className="PageBreak" />
          </>
          )
          return state.useSimpleForm ? showOnSimpleForm && sectionConst : sectionConst
        })}

        {!allInputsValid(state) && touched && <Errors state={state} setSettings={setSettings} />}
        {isFormComplete(state) && <Results state={state} setSettings={setSettings} />}

        <Stack direction="horizontal" gap={2} style={{ marginTop: "1rem" }} className="ExcludeFromExport">
          <Button
            tabIndex={90}
            style={{ backgroundColor: "var(--bs-primary)" }}
            variant="secondary"
            onClick={onChangeFormType}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onChangeFormType()}>
            {state.useSimpleForm ? "Switch to Advanced Form" : "Switch to Simple Form"}
          </Button>
          <Button
            tabIndex={91}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && resetFormValues()}
            onClick={resetFormValues}
            variant="link"
            disabled={!touched}
            style={{ marginLeft: "1rem", textDecoration: "none" }}>
            Reset All Values
          </Button>
        </Stack>
        <Alert variant="info" style={{ marginTop: "1rem" }} className="ExcludeFromExport">
          The simple form uses industry averages as the default values for most inputs. The advanced form allows you to specify your own values for each input.
        </Alert>
      </Accordion>
    </Form>
  )
}